<template>
  <div>
    <appHeader></appHeader>
    <appModal></appModal>
    <div class="container-fluid challenges-config">
      <div class="row">
        <div class="col-md-3 col-sm-12 pr-0 dark-bg">
          <appAdminSidebar></appAdminSidebar>
        </div>
        <transition appear name="fade">
          <div class="col-md-9 p-4 marginForHeader">
            <h4>Recognition Configuration</h4>
            
            <!-- Info Section -->
            <div class="alert alert-info" v-if="!loading && moduleEnabled && !isKudosEnabled">
              <h6 class="alert-heading">
                <font-awesome-icon icon="info-circle" class="me-2" />
                Configure Recognition
              </h6>
              <p class="mb-0">
                Enable and configure point values for different recognition. 
                Points will be awarded to employees when they complete these.
              </p>
            </div>

            <!-- Kudos Mode Info -->
            <div class="alert alert-warning" v-if="!loading && moduleEnabled && isKudosEnabled">
              <h6 class="alert-heading">
                <font-awesome-icon icon="star" class="me-2" />
                Kudos Points Mode Active
              </h6>
              <p class="mb-2">
                <strong>Recognition as Kudos Points (Non-Redeemable)</strong> is enabled for your organization.
              </p>
              <p class="mb-0">
                <font-awesome-icon icon="info-circle" class="me-1" />
                All points earned through peer-to-peer recognition will be <strong>non-redeemable kudos points</strong> for appreciation only. 
                Employees can accumulate these points to show recognition, but cannot redeem them for rewards.
              </p>
            </div>

            <!-- Module Disabled Warning -->
            <div class="alert alert-warning" v-if="!loading && !moduleEnabled">
              <h6 class="alert-heading">
                <font-awesome-icon icon="exclamation-triangle" class="me-2" />
                Recognition Module Not Enabled
              </h6>
              <p class="mb-0">
                The recognition module is not enabled for your organization. 
                Please contact your administrator to enable this feature.
              </p>
            </div>

            <!-- Challenges Configuration Table -->
            <div v-if="!loading" class="card shadow-sm mt-4">
              <div class="card-header bg-primary text-white">
                <h5 class="mb-0">
                  <font-awesome-icon icon="trophy" class="me-2" />
                  Available Recognition
                </h5>
              </div>
              <div class="card-body">
                <div class="table-responsive">
                  <table class="table table-hover">
                    <thead>
                      <tr>
                        <th style="width: 5%">Enabled</th>
                        <th style="width: 25%">Name</th>
                        <th style="width: 15%">Type</th>
                        <th style="width: 15%">Points</th>
                        <th style="width: 15%">Visibility</th>
                        <th style="width: 25%">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="challenge in challenges" :key="challenge.id">
                        <td class="text-center">
                          <div class="form-check form-switch">
                            <input
                              class="form-check-input"
                              type="checkbox"
                              :id="'challenge-' + challenge.id"
                              v-model="challenge.enabled"
                            />
                          </div>
                        </td>
                        <td>
                          <strong>{{ challenge.name }}</strong>
                          <span 
                            v-if="challenge.requires_integration" 
                            class="badge bg-warning ms-2"
                            title="Requires external integration"
                          >
                            <font-awesome-icon icon="plug" />
                          </span>
                        </td>
                        <td>
                          <span class="badge bg-info">{{ challenge.type }}</span>
                        </td>
                        <td>
                          <!-- Peer to Peer Recognition doesn't need points -->
                          <span v-if="challenge.isPeerToPeer" class="text-muted small">
                            <em>Based on user balance</em>
                          </span>
                          <input
                            v-else
                            type="number"
                            class="form-control form-control-sm"
                            v-model.number="challenge.points"
                            :disabled="!challenge.enabled"
                            min="0"
                            style="max-width: 100px"
                          />
                        </td>
                        <td>
                          <span class="badge" :class="challenge.visibility === 'Public' ? 'bg-success' : 'bg-secondary'">
                            {{ challenge.visibility }}
                          </span>
                        </td>
                        <td>
                          <small class="text-muted">
                            {{ challenge.description }}
                            <span v-if="challenge.isPeerToPeer" class="d-block mt-1 fw-bold text-info">
                              Users can give any amount based on their available point balance
                            </span>
                          </small>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div v-if="challenges.length === 0" class="text-center py-4">
                  <p class="text-muted">No data available</p>
                </div>

                <!-- Action Buttons -->
                <div class="d-flex justify-content-end gap-2 mt-4" v-if="challenges.length > 0">
                  <button 
                    type="button" 
                    class="btn btn-secondary"
                    @click="resetChanges"
                    :disabled="saving || !hasChanges"
                  >
                    <font-awesome-icon icon="undo" class="me-2" />
                    Reset Changes
                  </button>
                  <button 
                    type="button" 
                    class="btn btn-success"
                    @click="saveAllChallenges"
                    :disabled="saving || !hasChanges"
                  >
                    <font-awesome-icon v-if="saving" icon="spinner" spin class="me-2" />
                    <font-awesome-icon v-else icon="save" class="me-2" />
                    {{ saving ? 'Saving...' : 'Save All Changes' }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Loading Spinner -->
            <div v-if="loading" class="text-center py-5">
              <a-spin size="large" />
              <p class="mt-3 text-muted">Loading options...</p>
            </div>
          </div>
        </transition>
      </div>
    </div>
    <appFooter class="footer"></appFooter>

    <!-- Toast Notification Component -->
    <ToastNotification
      :show="toast.show"
      :message="toast.message"
      :type="toast.type"
      :duration="3000"
      @close="toast.show = false"
    />
  </div>
</template>

<script>
import Header from "@/components/Header.vue";
import Footer from "@/components/Footer.vue";
import AdminSidebar from "@/views/admin/AdminSidebar.vue";
import Modal from "@/components/Modal.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import RecognitionChallengesService from "@/services/RecognitionChallengesService";
import { mapState } from "vuex";

export default {
  name: "RecognitionChallengesConfig",
  
  components: {
    appHeader: Header,
    appFooter: Footer,
    appAdminSidebar: AdminSidebar,
    appModal: Modal,
    ToastNotification,
  },

  data() {
    return {
      loading: false,
      saving: false,
      moduleEnabled: true, // Will be determined from API response
      recognitionAsKudos: false, // Track if kudos mode is enabled
      challenges: [],
      originalChallenges: [],
      activeMenuItem: "recognitionChallenges",
      activeMenu: "recognition",
      toast: {
        show: false,
        message: "",
        type: "info"
      },
    };
  },

  computed: {
    ...mapState({
      userClients: (state) => state.user.userClients,
    }),

    clientId() {
      return this.userClients?.client || null;
    },

    hasChanges() {
      // Compare current challenges with original
      if (!this.originalChallenges || this.originalChallenges.length === 0) {
        return false;
      }

      return this.challenges.some((challenge, index) => {
        const original = this.originalChallenges[index];
        if (!original) return false;
        
        // Check if enabled status changed
        if (challenge.enabled !== original.enabled) {
          return true;
        }
        
        // Check if points changed (only for non-peer-to-peer)
        if (!challenge.isPeerToPeer && challenge.points !== original.points) {
          return true;
        }
        
        return false;
      });
    },

    isKudosEnabled() {
      return this.recognitionAsKudos === true;
    },
  },

  methods: {
    setActiveMenuItem() {
      this.$store.dispatch("adminSidebar/setActive", {
        activeMenuItem: this.activeMenuItem,
        activeMenu: this.activeMenu,
      });
    },

    async loadChallenges() {
      this.loading = true;
      try {
        const response = await RecognitionChallengesService.getClientChallenges(this.clientId);
        
        if (response.data.success) {
          this.challenges = response.data.data.map(challenge => ({
            ...challenge,
            // Peer to Peer Recognition doesn't need points configuration
            isPeerToPeer: challenge.name === 'Peer to Peer Recognition'
          }));
          // Deep clone for comparison
          this.originalChallenges = JSON.parse(JSON.stringify(this.challenges));
          this.moduleEnabled = true;
          
          // Check if kudos mode is enabled from response metadata
          if (response.data.metadata && response.data.metadata.recognition_as_kudos !== undefined) {
            this.recognitionAsKudos = response.data.metadata.recognition_as_kudos;
          }
        } else {
          if (response.data.message && response.data.message.includes('not enabled')) {
            this.moduleEnabled = false;
          }
          this.showToast(response.data.message || "Failed to load data", "error");
        }
      } catch (e) {
        console.error("Error loading data:", e);
        
        if (e.response && e.response.status === 403) {
          this.moduleEnabled = false;
          this.showToast("Recognition module is not enabled for your organization", "warning");
        } else {
          this.showToast("Error loading data. Please try again.", "error");
        }
      } finally {
        this.loading = false;
      }
    },

    async saveAllChallenges() {
      if (!this.hasChanges) {
        this.showToast("No changes to save", "info");
        return;
      }

      // Get all challenges that have changed
      const challengesToUpdate = this.challenges.filter((challenge, index) => {
        const original = this.originalChallenges[index];
        if (!original) return false;
        
        return challenge.enabled !== original.enabled || 
               (!challenge.isPeerToPeer && challenge.points !== original.points);
      });

      // Validate enabled challenges have points (except peer-to-peer)
      const invalidChallenges = challengesToUpdate.filter(c => 
        c.enabled && 
        !c.isPeerToPeer && 
        (!c.points || c.points < 0)
      );

      if (invalidChallenges.length > 0) {
        this.showToast("Please enter valid point values for all enabled recognition", "error");
        return;
      }

      this.saving = true;
      
      try {
        const payload = {
          client_id: this.clientId,
          challenges: challengesToUpdate.map(c => ({
            challenge_id: c.id,
            enabled: c.enabled,
            points: c.isPeerToPeer ? 0 : (c.points || 0),
            integration_config: c.integration_config ? JSON.stringify(c.integration_config) : null
          }))
        };

        const response = await RecognitionChallengesService.bulkConfigureChallenges(payload);
        
        if (response.data.success) {
          this.showToast("Setting saved successfully", "success");
          await this.loadChallenges();
        } else {
          this.showToast(response.data.message || "Failed to save setting", "error");
        }
      } catch (e) {
        console.error("Error saving setting:", e);
        this.showToast("Error saving setting. Please try again.", "error");
      } finally {
        this.saving = false;
      }
    },

    resetChanges() {
      this.challenges = JSON.parse(JSON.stringify(this.originalChallenges));
      this.showToast("Changes reset", "info");
    },

    showToast(message, type = "info") {
      this.toast.message = message;
      this.toast.type = type;
      this.toast.show = true;
    },
  },

  async created() {
    this.setActiveMenuItem();
    await this.$store.dispatch("user/fetchProfile");
    
    if (this.clientId) {
      await this.loadChallenges();
    } else {
      this.showToast("Client information not found", "error");
    }
  },
};
</script>

<style lang="scss" scoped>
.marginForHeader {
  margin-top: 50px;
}

.challenges-config {
  min-height: 100vh;
}

.card {
  border: 1px solid #e3e6f0;
  
  &.shadow-sm {
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  }
}

.card-header {
  &.bg-primary {
    background-color: #4e73df !important;
  }
}

.table {
  margin-bottom: 0;

  th {
    border-top: none;
    font-weight: 600;
    color: #5a5c69;
    font-size: 0.875rem;
    text-transform: uppercase;
  }

  td {
    vertical-align: middle;
  }
}

.form-check-input {
  width: 2.5rem;
  height: 1.25rem;
  cursor: pointer;

  &:checked {
    background-color: #1cc88a;
    border-color: #1cc88a;
  }
}

.badge {
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.35rem 0.65rem;
}

.gap-2 {
  gap: 0.5rem;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.alert {
  border-radius: 0.5rem;
}
.alert-heading {
    color: #0036b5;
}

</style>